/**
 * Action Modal Block Renderer
 *
 * Renders individual block types for level-specific action modal content.
 * Each block type has its own visual presentation optimized for the dark theme.
 */

import React, { useState } from 'react';
import { CheckCircle, ExternalLink, Shield, Clock, List, Lightbulb, Link as LinkIcon } from 'lucide-react';
import type { ActionModalBlock } from '../lib/actionModalBlockTypes';

interface BlockRendererProps {
  block: ActionModalBlock;
}

export function ActionModalBlockRenderer({ block }: BlockRendererProps) {
  switch (block.type) {
    case 'richText':
      return <RichTextBlockRenderer block={block} />;
    case 'bullets':
      return <BulletsBlockRenderer block={block} />;
    case 'reflectionPrompt':
      return <ReflectionPromptBlockRenderer block={block} />;
    case 'insightCard':
      return <InsightCardBlockRenderer block={block} />;
    case 'compareGrid':
      return <CompareGridBlockRenderer block={block} />;
    case 'timeline':
      return <TimelineBlockRenderer block={block} />;
    case 'connectionsList':
      return <ConnectionsListBlockRenderer block={block} />;
    case 'selectOne':
      return <SelectOneBlockRenderer block={block} />;
    case 'worldNoticingPrompt':
      return <WorldNoticingPromptBlockRenderer block={block} />;
    case 'comparePrompt':
      return <ComparePromptBlockRenderer block={block} />;
    case 'shortWrite':
      return <ShortWriteBlockRenderer block={block} />;
    case 'evidencePrompt':
      return <EvidencePromptBlockRenderer block={block} />;
    case 'audienceExplainerPrompt':
      return <AudienceExplainerPromptBlockRenderer block={block} />;
    case 'stakeholderMapPrompt':
      return <StakeholderMapPromptBlockRenderer block={block} />;
    case 'pathwaySelector':
      return <PathwaySelectorBlockRenderer block={block} />;
    case 'actionCards':
      return <ActionCardsBlockRenderer block={block} />;
    case 'contributionCards':
      return <ContributionCardsBlockRenderer block={block} />;
    case 'checklist':
      return <ChecklistBlockRenderer block={block} />;
    case 'planBuilder':
      return <PlanBuilderBlockRenderer block={block} />;
    case 'shareTemplate':
      return <ShareTemplateBlockRenderer block={block} />;
    case 'partnerLinks':
      return <PartnerLinksBlockRenderer block={block} />;
    case 'placeholder':
      return <PlaceholderBlockRenderer block={block} />;
    default:
      return null;
  }
}

function RichTextBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'richText' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="text-[14px] text-[#E0E0E0] leading-relaxed whitespace-pre-wrap">
        {block.body}
      </div>
    </div>
  );
}

function BulletsBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'bullets' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <ul className="space-y-2">
        {block.items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3 text-[14px] text-[#E0E0E0]">
            <span className="text-[#40E0D0] mt-1.5 flex-shrink-0">•</span>
            <span className="leading-relaxed">{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReflectionPromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'reflectionPrompt' }> }) {
  const [reflection, setReflection] = useState('');

  return (
    <div className="mb-6">
      <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <textarea
        value={reflection}
        onChange={(e) => setReflection(e.target.value)}
        placeholder={block.placeholder || 'Your thoughts...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[120px] resize-none"
        minLength={block.minChars}
        maxLength={block.maxChars}
      />
      {block.maxChars && (
        <div className="text-[12px] text-[#888888] mt-1 text-right">
          {reflection.length} / {block.maxChars}
        </div>
      )}
    </div>
  );
}

function InsightCardBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'insightCard' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
        <div className="flex items-start gap-3 mb-3">
          <Lightbulb className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
          <p className="text-[15px] font-medium text-[#F5F5F5] leading-relaxed">
            {block.statement}
          </p>
        </div>
        {block.supportingPoints && block.supportingPoints.length > 0 && (
          <ul className="space-y-2 mt-4 pl-8">
            {block.supportingPoints.map((point, idx) => (
              <li key={idx} className="text-[13px] text-[#CCCCCC] leading-relaxed">
                {point}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CompareGridBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'compareGrid' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
          <h5 className="text-[13px] font-semibold text-[#40E0D0] mb-3 uppercase tracking-wide">
            {block.leftLabel}
          </h5>
          <ul className="space-y-2">
            {block.leftItems.map((item, idx) => (
              <li key={idx} className="text-[13px] text-[#E0E0E0] leading-relaxed pl-3 border-l-2 border-[#40E0D0]/30">
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
          <h5 className="text-[13px] font-semibold text-[#40E0D0] mb-3 uppercase tracking-wide">
            {block.rightLabel}
          </h5>
          <ul className="space-y-2">
            {block.rightItems.map((item, idx) => (
              <li key={idx} className="text-[13px] text-[#E0E0E0] leading-relaxed pl-3 border-l-2 border-[#40E0D0]/30">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TimelineBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'timeline' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-4">{block.title}</h4>
      )}
      <div className="space-y-4">
        {block.events.map((event, idx) => (
          <div key={idx} className="flex gap-4">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-10 h-10 bg-[#40E0D0]/10 border-2 border-[#40E0D0] rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 text-[#40E0D0]" />
              </div>
              {idx < block.events.length - 1 && (
                <div className="w-0.5 h-full bg-[#282828] mt-2" />
              )}
            </div>
            <div className="flex-1 pb-4">
              <div className="text-[13px] font-semibold text-[#40E0D0] mb-1">{event.year}</div>
              <div className="text-[14px] font-medium text-[#F5F5F5] mb-1">{event.label}</div>
              {event.detail && (
                <div className="text-[13px] text-[#CCCCCC] leading-relaxed">{event.detail}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectionsListBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'connectionsList' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-3">
        {block.items.map((item, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4 hover:border-[#40E0D0]/30 transition-colors">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <h5 className="text-[14px] font-semibold text-[#F5F5F5]">{item.title}</h5>
                <p className="text-[13px] text-[#AAAAAA]">{item.artist}</p>
              </div>
              <LinkIcon className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-1" />
            </div>
            <p className="text-[13px] text-[#CCCCCC] leading-relaxed mb-2">{item.reason}</p>
            {item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag, tagIdx) => (
                  <span
                    key={tagIdx}
                    className="px-2 py-0.5 bg-[#40E0D0]/10 text-[#40E0D0] text-[11px] rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionCardsBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'actionCards' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-3">
        {block.items.map((item, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <h5 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">{item.title}</h5>
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="text-[#40E0D0]">{item.orgName}</span>
                  {item.region && (
                    <>
                      <span className="text-[#666666]">•</span>
                      <span className="text-[#888888]">{item.region}</span>
                    </>
                  )}
                  <span className="text-[#666666]">•</span>
                  <span className="px-2 py-0.5 bg-[#40E0D0]/10 text-[#40E0D0] rounded-full text-[11px]">
                    {item.actionType}
                  </span>
                </div>
              </div>
            </div>
            {item.whyThisOrg && (
              <p className="text-[13px] text-[#CCCCCC] leading-relaxed mb-3">{item.whyThisOrg}</p>
            )}
            {item.safetyNote && (
              <div className="bg-[#282828]/50 rounded-lg p-2 mb-3">
                <p className="text-[12px] text-[#AAAAAA] leading-relaxed">{item.safetyNote}</p>
              </div>
            )}
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[13px] text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
              >
                {item.actionType === 'Learn' ? 'Learn how' : 'Support if you choose'}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChecklistBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'checklist' }> }) {
  const [checked, setChecked] = useState<Record<number, boolean>>({});

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-2">
        {block.items.map((item, idx) => (
          <label
            key={idx}
            className="flex items-start gap-3 p-3 bg-[#0F0F0F] border border-[#282828] rounded-lg cursor-pointer hover:border-[#40E0D0]/30 transition-colors"
          >
            <input
              type="checkbox"
              checked={checked[idx] || false}
              onChange={() => setChecked({ ...checked, [idx]: !checked[idx] })}
              className="mt-1 w-4 h-4 rounded border-[#666666] bg-[#0F0F0F] text-[#40E0D0] focus:ring-[#40E0D0]/30"
            />
            <span className={`text-[13px] leading-relaxed ${checked[idx] ? 'text-[#888888] line-through' : 'text-[#E0E0E0]'}`}>
              {item.text}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function PlanBuilderBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'planBuilder' }> }) {
  const [plans, setPlans] = useState<Record<number, string>>({});

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-4">
        {block.steps.map((step, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-6 h-6 bg-[#40E0D0]/10 border border-[#40E0D0] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[12px] font-semibold text-[#40E0D0]">{idx + 1}</span>
              </div>
              <h5 className="text-[14px] font-semibold text-[#F5F5F5]">{step.title}</h5>
            </div>
            <textarea
              value={plans[idx] || ''}
              onChange={(e) => setPlans({ ...plans, [idx]: e.target.value })}
              placeholder={step.placeholder || 'Your plan...'}
              className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-3 text-[13px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[80px] resize-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareTemplateBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'shareTemplate' }> }) {
  const [copied, setCopied] = useState<number | null>(null);

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-3">
        {block.templates.map((template, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-[13px] font-semibold text-[#F5F5F5]">{template.label}</h5>
              <button
                onClick={() => handleCopy(template.templateText, idx)}
                className="px-3 py-1 text-[12px] bg-[#40E0D0]/10 text-[#40E0D0] hover:bg-[#40E0D0]/20 rounded-lg transition-colors"
              >
                {copied === idx ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-[13px] text-[#CCCCCC] leading-relaxed italic">
              {template.templateText}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerLinksBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'partnerLinks' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-3">
        {block.items.map((item, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h5 className="text-[14px] font-semibold text-[#F5F5F5]">{item.name}</h5>
                  {item.trustBadge && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-[#40E0D0]/10 text-[#40E0D0] text-[10px] rounded-full">
                      <Shield className="w-3 h-3" />
                      {item.trustBadge}
                    </span>
                  )}
                </div>
                {item.region && (
                  <span className="text-[12px] text-[#888888]">{item.region}</span>
                )}
              </div>
            </div>
            <p className="text-[13px] text-[#CCCCCC] leading-relaxed mb-3">{item.description}</p>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[13px] text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
            >
              Visit website
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaceholderBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'placeholder' }> }) {
  return (
    <div className="mb-6 bg-[#0F0F0F] border border-[#282828] rounded-xl p-6 text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        <List className="w-5 h-5 text-[#666666]" />
        <h4 className="text-[15px] font-semibold text-[#888888]">{block.title}</h4>
      </div>
      <p className="text-[13px] text-[#666666] leading-relaxed max-w-md mx-auto">
        {block.body}
      </p>
    </div>
  );
}

function SelectOneBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'selectOne' }> }) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <div className="space-y-2">
        {block.options.map((option) => (
          <button
            key={option.id}
            onClick={() => setSelected(option.id)}
            className={`w-full text-left p-4 rounded-lg border transition-all ${
              selected === option.id
                ? 'bg-[#40E0D0]/10 border-[#40E0D0] text-[#F5F5F5]'
                : 'bg-[#0F0F0F] border-[#282828] text-[#E0E0E0] hover:border-[#40E0D0]/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selected === option.id ? 'border-[#40E0D0]' : 'border-[#666666]'
                }`}
              >
                {selected === option.id && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#40E0D0]" />
                )}
              </div>
              <span className="text-[14px] leading-relaxed">{option.text}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function WorldNoticingPromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'worldNoticingPrompt' }> }) {
  const [response, setResponse] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder={block.placeholder || 'Your observations...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[100px] resize-none"
      />
    </div>
  );
}

function ComparePromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'comparePrompt' }> }) {
  const [response, setResponse] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="bg-[#0F0F0F] border border-[#282828] rounded-lg p-3">
          <div className="text-[12px] font-semibold text-[#40E0D0] mb-2 uppercase tracking-wide">
            {block.leftLabel}
          </div>
          <div className="h-16 text-[13px] text-[#888888]">Add context...</div>
        </div>
        <div className="bg-[#0F0F0F] border border-[#282828] rounded-lg p-3">
          <div className="text-[12px] font-semibold text-[#40E0D0] mb-2 uppercase tracking-wide">
            {block.rightLabel}
          </div>
          <div className="h-16 text-[13px] text-[#888888]">Add context...</div>
        </div>
      </div>
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder={block.placeholder || 'Describe the comparison...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[100px] resize-none"
      />
    </div>
  );
}

function ShortWriteBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'shortWrite' }> }) {
  const [response, setResponse] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder={block.placeholder || 'Your synthesis...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[100px] resize-none"
        minLength={block.minChars}
        maxLength={block.maxChars}
      />
      {block.maxChars && (
        <div className="text-[12px] text-[#888888] mt-1 text-right">
          {response.length} / {block.maxChars}
        </div>
      )}
    </div>
  );
}

function EvidencePromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'evidencePrompt' }> }) {
  const [evidence, setEvidence] = useState('');
  const [url, setUrl] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      <textarea
        value={evidence}
        onChange={(e) => setEvidence(e.target.value)}
        placeholder={block.placeholder || 'Describe the example you found...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[100px] resize-none mb-3"
      />
      {block.requiresUrl && (
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Link (optional)"
          className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0]"
        />
      )}
    </div>
  );
}

function AudienceExplainerPromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'audienceExplainerPrompt' }> }) {
  const [selectedAudience, setSelectedAudience] = useState('');
  const [explanation, setExplanation] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      {block.audienceOptions && block.audienceOptions.length > 0 && (
        <select
          value={selectedAudience}
          onChange={(e) => setSelectedAudience(e.target.value)}
          className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] mb-3"
        >
          <option value="">Select audience...</option>
          {block.audienceOptions.map((audience, idx) => (
            <option key={idx} value={audience}>
              {audience}
            </option>
          ))}
        </select>
      )}
      <textarea
        value={explanation}
        onChange={(e) => setExplanation(e.target.value)}
        placeholder={block.placeholder || 'How would you explain this...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[120px] resize-none"
      />
    </div>
  );
}

function StakeholderMapPromptBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'stakeholderMapPrompt' }> }) {
  const [mapping, setMapping] = useState('');

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      <p className="text-[14px] text-[#CCCCCC] mb-3">{block.prompt}</p>
      {block.categories && block.categories.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {block.categories.map((category, idx) => (
            <div
              key={idx}
              className="bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-center"
            >
              <div className="text-[12px] font-semibold text-[#40E0D0] uppercase tracking-wide">
                {category}
              </div>
            </div>
          ))}
        </div>
      )}
      <textarea
        value={mapping}
        onChange={(e) => setMapping(e.target.value)}
        placeholder={block.placeholder || 'Map the stakeholders...'}
        className="w-full bg-[#0F0F0F] border border-[#282828] rounded-lg p-3 text-[14px] text-[#F5F5F5] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/30 focus:border-[#40E0D0] min-h-[120px] resize-none"
      />
    </div>
  );
}

function PathwaySelectorBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'pathwaySelector' }> }) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-2">{block.title}</h4>
      )}
      {block.description && (
        <p className="text-[14px] text-[#CCCCCC] mb-4">{block.description}</p>
      )}
      <div className="space-y-3">
        {block.pathways.map((pathway) => (
          <button
            key={pathway.id}
            onClick={() => setSelected(pathway.id)}
            className={`w-full text-left p-4 rounded-lg border transition-all ${
              selected === pathway.id
                ? 'bg-[#40E0D0]/10 border-[#40E0D0]'
                : 'bg-[#0F0F0F] border-[#282828] hover:border-[#40E0D0]/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selected === pathway.id ? 'border-[#40E0D0]' : 'border-[#666666]'
                }`}
              >
                {selected === pathway.id && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#40E0D0]" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                  {pathway.label}
                </div>
                <div className="text-[13px] text-[#CCCCCC] leading-relaxed">
                  {pathway.description}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ContributionCardsBlockRenderer({ block }: { block: Extract<ActionModalBlock, { type: 'contributionCards' }> }) {
  return (
    <div className="mb-6">
      {block.title && (
        <h4 className="text-[15px] font-semibold text-[#40E0D0] mb-3">{block.title}</h4>
      )}
      <div className="space-y-3">
        {block.items.map((item, idx) => (
          <div key={idx} className="bg-[#0F0F0F] border border-[#282828] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <h5 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">{item.title}</h5>
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="text-[#40E0D0]">{item.partnerName}</span>
                  {item.region && (
                    <>
                      <span className="text-[#666666]">•</span>
                      <span className="text-[#888888]">{item.region}</span>
                    </>
                  )}
                  <span className="text-[#666666]">•</span>
                  <span className="px-2 py-0.5 bg-[#40E0D0]/10 text-[#40E0D0] rounded-full text-[11px]">
                    {item.contributionType}
                  </span>
                </div>
              </div>
            </div>
            {item.expectations && (
              <p className="text-[13px] text-[#CCCCCC] leading-relaxed mb-3">{item.expectations}</p>
            )}
            {item.safetyNote && (
              <div className="bg-[#282828]/50 rounded-lg p-2 mb-3">
                <p className="text-[12px] text-[#AAAAAA] leading-relaxed">{item.safetyNote}</p>
              </div>
            )}
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[13px] text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
              >
                Learn more
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
